import { getBasePath } from "@/lib/apiConfig";
import type { SessionEndReason } from "@/lib/sessionChannel";

const SESSION_COOKIE_NAME = "pbx_session";
const SESSION_COOKIE_MAX_AGE = 60 * 60 * 24 * 7; // 7 days

/**
 * Display metadata shared with the marketing site header. Deliberately credential-free
 * (CT-36): the access token is memory-only and the refresh credential is the HttpOnly
 * __Host-pbx_rt cookie on the pbx-api origin.
 */
export type SessionData = {
    first_name: string;
    last_name: string;
    avatar: string | null;
    subdomain: string;
    role: string;
};

/**
 * UTF-8-safe base64 encode. `btoa` only accepts Latin1 (code points 0–255) and
 * throws on multibyte characters (e.g. Japanese names), so encode to UTF-8 bytes first.
 */
function base64EncodeUtf8(value: string): string {
    const bytes = new TextEncoder().encode(value);
    let binary = "";
    for (const byte of bytes) {
        binary += String.fromCharCode(byte);
    }
    return btoa(binary);
}

/**
 * Inverse of {@link base64EncodeUtf8}: base64 → Latin1 bytes → UTF-8 string.
 */
function base64DecodeUtf8(encoded: string): string {
    const binary = atob(encoded);
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
    return new TextDecoder().decode(bytes);
}

/**
 * Get root domain for cookie (e.g., ".pbx.test" from "tenant.pbx.test")
 * Returns null for public suffixes (vercel.app, etc.) where cross-subdomain cookies aren't allowed
 */
function getRootDomain(): string | null {
    const host = window.location.hostname;

    // Public suffixes where we can't set cross-subdomain cookies
    if (host.endsWith(".vercel.app")) {
        return null;
    }

    const parts = host.split(".");
    if (parts.length <= 2) {
        return host;
    }
    return parts.slice(-2).join(".");
}

/**
 * Get the return path from URL params or use default. Only same-origin paths are honored:
 * a single leading "/" (never "//host" or an absolute URL), closing the ?from= open
 * redirect for good.
 */
export function getReturnPath(): string {
    const urlParams = new URLSearchParams(window.location.search);
    const fromParam = urlParams.get("from");
    if (fromParam && /^\/(?!\/)/.test(fromParam)) {
        return fromParam;
    }
    return "/dashboard";
}

export function readSessionCookie(): SessionData | null {
    if (typeof document === "undefined") return null;
    const match = document.cookie.split(";").find((c) => c.trim().startsWith(`${SESSION_COOKIE_NAME}=`));
    if (!match) return null;
    try {
        return JSON.parse(base64DecodeUtf8(match.trim().split("=")[1])) as SessionData;
    } catch {
        return null;
    }
}

export function setSessionCookie(sessionData: SessionData): void {
    const encoded = base64EncodeUtf8(JSON.stringify(sessionData));
    const rootDomain = getRootDomain();
    const domainPart = rootDomain ? `domain=.${rootDomain};` : "";
    const securePart = window.location.protocol === "https:" ? "secure;" : "";
    document.cookie = `${SESSION_COOKIE_NAME}=${encoded}; ${domainPart} path=/; max-age=${SESSION_COOKIE_MAX_AGE}; ${securePart} samesite=lax`;
}

export function clearSessionCookie(): void {
    const rootDomain = getRootDomain();
    const domainPart = rootDomain ? `domain=.${rootDomain};` : "";
    document.cookie = `${SESSION_COOKIE_NAME}=; ${domainPart} path=/; max-age=0`;
}

/**
 * Redirect to login page ("/login", under the base path).
 *
 * `reason` rides as a query param so the reloaded login page can tell the user WHY it is
 * looking at a login form (useAuthToasts reads and strips it). Per-tab by construction —
 * every tab gets its own URL, unlike a shared persisted toast.
 */
export function redirectToLogin(reason?: SessionEndReason) {
    const query = reason ? `?reason=${encodeURIComponent(reason)}` : "";
    window.location.href = `${getBasePath()}/login${query}`;
}
